// web/shims/DateTimePickerShim.tsx
// Shim for @react-native-community/datetimepicker on web
import React from 'react';
import { View, StyleSheet } from 'react-native';

type DateTimePickerMode = 'date' | 'time' | 'datetime';

interface DateTimePickerProps {
  value: Date;
  mode?: DateTimePickerMode;
  display?: string;
  onChange?: (event: any, date?: Date) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  style?: any;
  inputStyle?: React.CSSProperties;
}

// Format date to YYYY-MM-DD for input[type="date"]
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format time to HH:MM for input[type="time"]
const formatTimeForInput = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  mode = 'date',
  onChange,
  minimumDate,
  maximumDate,
  style,
  inputStyle,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!newValue) return;

    let newDate: Date;
    if (mode === 'time') {
      const [hours, minutes] = newValue.split(':').map(Number);
      newDate = new Date(value);
      newDate.setHours(hours, minutes);
    } else {
      newDate = new Date(newValue);
      // Preserve time when changing date
      newDate.setHours(
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
      );
    }

    onChange?.(
      { type: 'set', nativeEvent: { timestamp: newDate.getTime() } },
      newDate,
    );
  };

  const handleClick = () => {
    // Open the native date picker when clicking anywhere on the input
    inputRef.current?.showPicker?.();
  };

  const inputType = mode === 'time' ? 'time' : 'date';
  const inputValue =
    mode === 'time' ? formatTimeForInput(value) : formatDateForInput(value);

  return (
    <View style={[styles.container, style]}>
      <style>
        {`
          .bpDateTimePickerInput::-webkit-clear-button,
          .bpDateTimePickerInput::-webkit-inner-spin-button,
          .bpDateTimePickerInput::-webkit-calendar-picker-indicator {
            -webkit-appearance: none;
          }
          .bpDateTimePickerInput::-webkit-clear-button {
            display: none;
          }
          .bpDateTimePickerInput::-ms-clear {
            display: none;
            width: 0;
            height: 0;
          }
          /* Keep calendar icon visible/clickable */
          .bpDateTimePickerInput::-webkit-calendar-picker-indicator {
            display: block;
            opacity: 1;
            cursor: pointer;
          }
        `}
      </style>
      <input
        ref={inputRef}
        className="bpDateTimePickerInput"
        type={inputType}
        value={inputValue}
        onChange={handleChange}
        onClick={handleClick}
        min={
          minimumDate
            ? mode === 'time'
              ? formatTimeForInput(minimumDate)
              : formatDateForInput(minimumDate)
            : undefined
        }
        max={
          maximumDate
            ? mode === 'time'
              ? formatTimeForInput(maximumDate)
              : formatDateForInput(maximumDate)
            : undefined
        }
        style={{
          width: '100%',
          padding: 16,
          fontSize: 16,
          borderRadius: 8,
          border: '1px solid #79747E',
          backgroundColor: '#fff',
          color: '#1C1B1F',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
          height: 56,
          outline: 'none',
          cursor: 'pointer',
          ...(inputStyle || {}),
        }}
        onFocus={e => {
          e.target.style.borderColor = '#0D47A1';
          e.target.style.borderWidth = '2px';
        }}
        onBlur={e => {
          e.target.style.borderColor = '#79747E';
          e.target.style.borderWidth = '1px';
        }}
      />
    </View>
  );
};

// Stub for DateTimePickerAndroid - noop on web
export const DateTimePickerAndroid = {
  open: (_options: any) => {
    console.warn('DateTimePickerAndroid.open is not supported on web');
  },
  dismiss: (_mode: string) => {
    console.warn('DateTimePickerAndroid.dismiss is not supported on web');
  },
};

// Export types to match the native module
export type { DateTimePickerProps as DateTimePickerEvent };

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
});

export default DateTimePicker;
